import React from 'react';
import {withTranslation} from 'react-i18next';
import {Field, Form as FinalForm} from 'react-final-form';
import {Button, Col, CustomInput, Form, Input, Row} from 'reactstrap';
import TextField from '../components/TextField';
import LoaderButton from '../components/LoaderButton';
import ModalManager from '../components/modal/ModalManager';
import {NotificationManager} from "../components/common/react-notifications";
import {dataURItoBlob, difference, getDatePickerProps, numberWithCommas, stringToNumber} from "../helpers/Utils";
import {composeValidators, mustBeInteger, mustBeNumber, required} from "../helpers/Validators";
import {capitalize, find, forEach, isEqual, trim} from 'lodash';
import FieldError from "../components/FieldError";
import FileField from "../components/FileField";
import Switch from 'react-switch';
import Api from "../services/Api";
import {createMuiTheme, ThemeProvider} from "@material-ui/core/styles";
import {DateTimePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import i18n from "../services/i18n";
import fa_IR from "../constants/locales";
import JalaliUtils from "@date-io/jalaali";
import MomentUtils from "@date-io/moment";
import moment from "moment";

class TokenManageForm extends React.Component {
  addToken = async values => {
    const {t, afterSubmit} = this.props;
    try {
      const {symbol, enName, faName, displayDecimal, decimalPrecision, tokenType, active, transferable, logoUrl, releasedAt} = values;
      const formData = new FormData();
      formData.append("symbol", String(trim(symbol)).toUpperCase());
      formData.append("decimalPrecision", String(decimalPrecision));
      formData.append("displayDecimal", String(displayDecimal));
      formData.append("labels", JSON.stringify([
        {locale: "en", value: capitalize(trim(enName))},
        {locale: "fa", value: capitalize(trim(faName))}
      ]));
      formData.append("tokenType", tokenType);
      formData.append("active", String(active));
      formData.append("transferable", String(transferable));
      formData.append("image", dataURItoBlob(logoUrl));
      formData.append("releasedAt", moment.utc(releasedAt).toISOString());

      await Api.invoke('POST', '/v2/admin-ui/tokens', formData, undefined);
      afterSubmit();

      NotificationManager.success(t('Token {{symbol}} added successfully', {
        symbol: String(trim(symbol)).toUpperCase(),
      }), t('Message'));
      ModalManager.close({
        isOpen: false
      });
    } catch (e) {
      if (e.fieldError) {
        return e.fieldError;
      }
      throw e;
    }
  };

  updateToken = async values => {
    const {t, token, afterSubmit} = this.props;
    const data = {
      releasedAt: moment.utc(values.releasedAt).toISOString(),
      decimalPrecision: Number(values.decimalPrecision),
      displayDecimal: Number(values.displayDecimal),
      transferable: values.transferable,
      tokenType: values.tokenType,
      active: values.active,
      logoUrl: values.logoUrl,
    };
    const diff = difference(data, token);
    const newValues = {
      ...diff,
      labels:  [
        {locale: "en", value: capitalize(trim(values.enName))},
        {locale: "fa", value: capitalize(trim(values.faName))},
      ],
    }
    const formData = new FormData();
    forEach(newValues, (value, key) => {
      if (key === 'logoUrl') {
        formData.append("image", dataURItoBlob(value));
      } else if (key === "releasedAt") {
        formData.append("releasedAt", moment.utc(values.releasedAt).toISOString());
      } else {
        formData.append(key, JSON.stringify(value));
      }
    });
    try {
      await Api.invoke(
        'PUT',
        `/v2/admin-ui/tokens/${token.symbol}`,
        formData,
      );
      NotificationManager.success(t('Token {{symbol}} updated successfully', {symbol: token.symbol}), t('Message'));
      afterSubmit();
      ModalManager.close({
        isOpen: false
      });
    } catch (e) {
      if (e.fieldError) {
        return e.fieldError;
      }
      throw e;
    }
  };

  render() {
    const {t, token} = this.props;
    return(
      <ThemeProvider
        theme={createMuiTheme({
          typography: {
            fontFamily: 'IRANSans',
          }
        })}
      >
        <FinalForm
          initialValues={{
            enName: token && token.labels.length ? find(token.labels, {locale: "en"}).value : undefined,
            faName: token && token.labels.length ? find(token.labels, {locale: "fa"}).value : undefined,
            tokenType: token ? token.tokenType : "CRYPTO",
            decimalPrecision: token ? String(token.decimalPrecision) : "10",
            displayDecimal: token ? String(token.displayDecimal) : "6",
            transferable: token ? token.transferable : true,
            active: token ? token.active : false,
            logoUrl: token ? token.logoUrl : undefined,
            releasedAt: token && token.releasedAt ? moment.utc(token.releasedAt).toISOString() : undefined,
          }}
          onSubmit={token ? this.updateToken : this.addToken}
          render={({handleSubmit, submitting, hasValidationErrors, values, form, initialValues}) => {
            return (
              <Form onSubmit={handleSubmit}>
                <Row className='mb-3'>
                  {!token && <Col className="col-lg-6">
                    <TextField item={{
                      name: 'symbol',
                      i18n: t('Symbol'),
                      type: 'text',
                      required: true,
                    }}/>
                  </Col>}
                </Row>
                <Row className='mb-3'>
                  <Col>
                    <TextField item={{
                      name: 'enName',
                      i18n: t('English Name'),
                      type: 'text',
                      required: true,
                    }}/>
                  </Col>
                  <Col>
                    <TextField item={{
                      name: 'faName',
                      i18n: t('Farsi Name'),
                      type: 'text',
                      required: true,
                    }}/>
                  </Col>
                </Row>
                <Row className='mb-3'>
                  <Col>
                    <Field name="decimalPrecision" validate={composeValidators(required, mustBeNumber, mustBeInteger)}>
                      {({input}) => (
                        <div>
                          <span>{t('Decimal Precision')}<span className="required">*</span></span>
                          <Input
                            {...input}
                            value={numberWithCommas(input.value)}
                            onChange={e => {input.onChange(stringToNumber(e.target.value));}}
                          />
                          <FieldError name="decimalPrecision"/>
                        </div>
                      )}
                    </Field>
                  </Col>
                  <Col>
                    <Field name="displayDecimal" validate={composeValidators(required, mustBeNumber, mustBeInteger)}>
                      {({input}) => (
                        <div>
                          <span>{t('Display Decimal')}<span className="required">*</span></span>
                          <Input
                            {...input}
                            value={numberWithCommas(input.value)}
                            onChange={e => {input.onChange(stringToNumber(e.target.value));}}
                          />
                          <FieldError name="displayDecimal"/>
                        </div>
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row className='align-items-center mb-3'>
                  <Col>
                    <FileField
                      item={{
                        name: "logoUrl",
                        i18n: t("logo"),
                        path: values.logoUrl,
                        accept: "image/svg+xml",
                        previewSize: "60px",
                        required: true,
                      }}
                    />
                  </Col>
                </Row>
                <Row className='align-items-center mb-4'>
                  <Col>
                    <span className="mb-1">{t('Estimated List Date')}<span className="required">*</span></span>
                    <Field name="releasedAt" validate={required}>
                      {({input}) => (
                        <MuiPickersUtilsProvider
                          utils={i18n.language === fa_IR ? JalaliUtils : MomentUtils}
                          locale={i18n.language}>
                          <DateTimePicker
                            {...getDatePickerProps()}
                            minDate={moment()}
                            maxDate={undefined}
                            value={input.value ? input.value : null}
                            onChange={date => {input.onChange(date);}}
                            TextFieldComponent={props =>
                              <Input
                                style={{width: '100%'}}
                                placeholder={t('Select Date')}
                                {...props}
                              />
                            }
                          />
                        </MuiPickersUtilsProvider>
                      )}
                    </Field>
                  </Col>
                </Row>
                <Row className='ml-0 mb-3' style={{fontSize: 14}}>
                  <CustomInput
                    id="smtp"
                    type="radio"
                    name="customRadio"
                    label={t('Crypto Token')}
                    checked={values.tokenType === 'CRYPTO'}
                    onChange={() => {form.change('tokenType', 'CRYPTO');}}
                  />
                  <CustomInput
                    type="radio"
                    id="mailgun"
                    className='mx-4'
                    name="customRadio"
                    label={t('Fiat Token')}
                    checked={values.tokenType === 'FIAT'}
                    onChange={() => {form.change('tokenType', 'FIAT');}}
                  />
                </Row>
                <Row className='align-items-center mb-3'>
                  <Switch
                    className="mx-3 ltr"
                    onChange={enabled => {form.change('active', enabled);}}
                    checked={values.active}
                    height={24}
                    width={45}
                  />
                  <span className={`mx-2 ${values.active ? 'text-success' : 'text-danger'}`}>
                    {t('Status')} {values.active ? t('Enabled') : t('Disabled')}
                  </span>
                </Row>
                <Row className='align-items-center'>
                  <Switch
                    className="mx-3 ltr"
                    onChange={enabled => {form.change('transferable', enabled);}}
                    checked={values.transferable}
                    height={24}
                    width={45}
                  />
                  <span className={`mx-2 ${values.transferable ? 'text-success' : 'text-danger'}`}>
                    {t('Transferability')}
                  </span>
                </Row>
                <Row className='mt-5'>
                  <Col>
                    <LoaderButton
                      color="success"
                      className="text-center"
                      loading={submitting}
                      onClick={handleSubmit}
                      style={{minWidth: '100px'}}
                      disabled={hasValidationErrors || submitting || isEqual(values, initialValues)}>
                      {token ? t('Update') : t('Submit')}
                    </LoaderButton>
                    <Button
                      color="grey"
                      onClick={() =>{
                        ModalManager.close({
                          isOpen: false
                        });
                      }}
                      className="text-center mx-2"
                      style={{minWidth: '100px'}}>
                      {t('Close')}
                    </Button>
                  </Col>
                </Row>
              </Form>
            );
          }}
        />
      </ThemeProvider>
    );
  }
}

export default withTranslation()(TokenManageForm);
